import React, {
  ChangeEvent,
  MouseEvent,
  ReactNode,
  useCallback,
  useMemo,
  useState,
} from 'react';
import { useSeqConsultContext } from '@/providers/SeqConsultProvider';
import { useFormContext } from 'react-hook-form';
import { usePopup } from '@/providers/PopupProvider/PopupProvider';
import PersonalInfoCollection from '@/components/AgreementPopup/PersonalInfoCollection';
import ThirdParties from '@/components/AgreementPopup/ThirdParties';
import useAuthenticated from '@/hooks/useAuthenticated';
import { quickConsultGateway } from '@/di-container/remodeling-manager/quick-consult/gateway/client';
import useSWR from 'swr';
import {
  MATCH_CONSULT_DATA,
  SEQ_CONSULT_DATA,
  SEQ_CONSULT_NEXT_BUTTON_ID,
  SPACE_SCOPES,
} from '@/constants/seq-consult';
import useTelCertification from '@/hooks/useTelCertification';
import { SEQ_CONSULT_KAKAO_TEMPLATE_CODE } from '@/types/seq-consult';
import { TOAST_Z_INDEX } from '@/styles';
import { useToast } from '@design-system/pc';
import useSeqConsult from '../hooks/useSeqConsult';

const TERMS: {
  id: 'privacy' | 'provide';
  text: string;
  popupTitle: string;
  popup: ReactNode;
}[] = [
  {
    id: 'privacy',
    text: '개인정보 수집 및 이용동의',
    popupTitle: '개인정보 수집 및 이용동의',
    popup: <PersonalInfoCollection kind="quick" />,
  },
  {
    id: 'provide',
    text: '개인정보 제3자 제공 동의',
    popupTitle: '개인정보 제3자 제공 동의',
    popup: <ThirdParties kind="quick" />,
  },
];

const useSeqConsultQuestion = () => {
  const {
    register,
    getValues,
    setValue,
    watch,
    resetField,
    trigger,
    formState: { errors },
  } = useFormContext();

  const { step, consultType, handleChangeTelCertificated } =
    useSeqConsultContext();
  const { handleOpen } = usePopup();
  const { isAuthenticated } = useAuthenticated();
  const { getBasic } = quickConsultGateway();
  const { data: userInfoResponse } = useSWR(
    isAuthenticated ? 'seq-user-info' : null,
    () => getBasic.exec(),
  );
  const [allScopeChecked, setAllScopeChecked] = useState(false);

  const currentConsultData = useMemo(() => {
    return consultType === 'SEQ' ? SEQ_CONSULT_DATA : MATCH_CONSULT_DATA;
  }, [consultType]);

  const currentStepData = useMemo(() => {
    if (step in currentConsultData) {
      return currentConsultData[step as keyof typeof currentConsultData];
    }
    return null;
  }, [currentConsultData, step]);

  const user = useMemo(() => {
    return {
      name: userInfoResponse?.data?.user.name,
      tel: userInfoResponse?.data?.user.tel,
    };
  }, [userInfoResponse?.data?.user.name, userInfoResponse?.data?.user.tel]);

  const getActive = useCallback(
    <T extends string | number>(value: T) => {
      if (step === 'baseConstructionPlan.scopes') {
        return watch('baseConstructionPlan.scopes')?.includes(value);
      }

      return watch(step) === value;
    },
    [step, watch],
  );

  const handleClick = useCallback(
    <T extends string | number>(value: T) => {
      if (value === 'ETC') {
        resetField('baseConstructionPlan.etcComment');
      }

      if (step === 'baseConstructionPlan.scopes') {
        const prevValue: T[] = getValues(step) ? getValues(step) : [];

        if (prevValue.includes(value as T) && value) {
          setValue(
            step,
            prevValue.filter((_value) => _value !== value),
          );

          if (value !== 'ETC') {
            setAllScopeChecked(false);
          }
        } else {
          const nextScopes = [...prevValue, value];
          const isAllScopeWithoutETC = nextScopes.filter(
            (scope) => scope !== 'ETC',
          );
          setValue(step, nextScopes);
          if (isAllScopeWithoutETC.length === 11) {
            setAllScopeChecked(true);
          }
        }
      }

      if (step !== 'baseConstructionPlan.scopes') {
        if (getValues(step) === value) {
          setValue(step, undefined);
        } else {
          setValue(step, value);
        }
      }

      if (currentStepData?.autoNext && getActive(value)) {
        setTimeout(() => {
          const nextButtonEl = document.getElementById(
            SEQ_CONSULT_NEXT_BUTTON_ID,
          );
          nextButtonEl?.click?.();
        }, 300);
      }
    },
    [getValues, setValue, step, getActive, resetField, currentStepData],
  );

  const isShowEtcInput = watch('baseConstructionPlan.scopes')?.includes('ETC');

  const handleChangeAllScopes = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const isChecked = e.currentTarget.checked;
      setAllScopeChecked(isChecked);
      const hasEtc = getValues('baseConstructionPlan.scopes')?.includes('ETC');

      if (isChecked) {
        const allScopes = SPACE_SCOPES.data
          .filter((item) => item.value !== 'ETC')
          .map((item) => item.value);

        if (hasEtc) {
          allScopes.push('ETC');
        }
        setValue('baseConstructionPlan.scopes', allScopes);
      } else {
        if (hasEtc) {
          setValue('baseConstructionPlan.scopes', ['ETC']);
        } else {
          setValue('baseConstructionPlan.scopes', []);
        }
      }
    },
    [getValues, setValue],
  );

  const [allAgreementChecked, setIsAllAgreementChecked] = useState(false);

  const handleAllAgreement = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const isChecked = e.currentTarget.checked;
      setIsAllAgreementChecked(isChecked);

      if (isChecked) {
        setValue('agreement.privacy', true);
        setValue('agreement.provide', true);
      } else {
        setValue('agreement.privacy', false);
        setValue('agreement.provide', false);
      }
    },
    [setValue],
  );

  const handleAgreementChecked = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const targetName = e.currentTarget.name;
      const isChecked = e.currentTarget.checked;
      setValue(targetName, isChecked);

      if (getValues('agreement.privacy') && getValues('agreement.provide')) {
        setIsAllAgreementChecked(true);
      } else {
        setIsAllAgreementChecked(false);
      }
    },
    [getValues, setValue],
  );

  const handleAgreementPopup = useCallback(
    (e: MouseEvent<HTMLButtonElement>) => {
      e.preventDefault();
      const targetName = e.currentTarget.name;
      const findTerm = TERMS.find((term) => term.id === targetName);

      if (findTerm) {
        handleOpen({
          popupProps: {
            title: findTerm.popupTitle,
            popupSize: 'md',
            buttonType: 'noneBtn',
            children: findTerm.popup,
          },
        });
      }
    },
    [handleOpen],
  );

  const handleChangeTel = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const { value } = e.currentTarget;
      const tel = value
        .replace(/[^0-9]/g, '')
        .replace(/^(\d{0,3})(\d{0,4})(\d{0,4})$/g, '$1-$2-$3')
        .replace(/(\-{1,2})$/g, ''); // eslint-disable-line
      setValue('tel', tel);
    },
    [setValue],
  );
  const handleChangeEtc = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      let { value } = e.currentTarget;
      if (value.length > 20) {
        value = value.slice(0, 20);
      }
      if (value.trim().length === 0) {
        setValue(
          'baseConstructionPlan.etcComment',
          value.replace(/^\s*/, '').replace(/\s*$/, ''),
        );
      } else {
        setValue('baseConstructionPlan.etcComment', value);
      }
    },
    [setValue],
  );

  const handleChangeName = (e: React.FormEvent<HTMLInputElement>) => {
    let { value } = e.currentTarget;

    if (value.length > 20) {
      value = value.slice(0, 20);
    }

    if (value.trim().length === 0) {
      setValue('name', value.replace(/^\s*/, '').replace(/\s*$/, ''));
    } else {
      setValue('name', value);
    }
  };

  const { handleSend, handleCertification, handleResetStatus, count, status } =
    useTelCertification({
      templateCode: SEQ_CONSULT_KAKAO_TEMPLATE_CODE,
    });

  const [certificationNumber, setCertificationNumber] = useState('');
  const [isTelDisabled, setIsTelDisabled] = useState(false);

  const handleChangeCertificationNumber = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      if (e.target.value.length > 6) {
        return;
      }
      setCertificationNumber(e.target.value);
    },
    [],
  );

  const { toast } = useToast();

  const handleSendTel = useCallback(async () => {
    const isPassed = await trigger('tel');

    if (isPassed) {
      const response = await handleSend(getValues('tel'));
      if (response?.code === 200) {
        toast.success('인증번호가 전송 되었습니다.', {
          alignment: 'center',
          placement: 'bottom',
          variant: 'basic',
          offset: 40,
          zIndex: TOAST_Z_INDEX,
          duration: 3,
        });
      }
    }
  }, [getValues, handleSend, toast, trigger]);

  const handleSendCertificationNumber = useCallback(async () => {
    setIsTelDisabled(false);

    const response = await handleCertification(
      getValues('tel'),
      certificationNumber,
    );

    if (response?.code === 200) {
      toast.success('인증이 완료 되었습니다.', {
        alignment: 'center',
        placement: 'bottom',
        variant: 'basic',
        offset: 40,
        zIndex: TOAST_Z_INDEX,
        duration: 3,
      });
      setIsTelDisabled(true);
      handleChangeTelCertificated(true);
      setCertificationNumber('');
    }
  }, [
    certificationNumber,
    getValues,
    handleCertification,
    handleChangeTelCertificated,
    toast,
  ]);

  const handleEnableTelInput = useCallback(() => {
    handleResetStatus();
    setValue('tel', '');
    setIsTelDisabled(false);
    handleChangeTelCertificated(false);
  }, [handleChangeTelCertificated, handleResetStatus, setValue]);

  const {
    workAvailableAreas,
    isOpenWorkableBottomSheet,
    handleOpenBottomSheet,
    handleCloseBottomSheet,
  } = useSeqConsult();

  return {
    handleClick,
    register,
    watch,
    step,
    isShowEtcInput,
    allFlagChecked: allScopeChecked,
    handleChangeAllScopes,
    getActive,
    errors,
    handleAllAgreement,
    allAgreementChecked,
    handleAgreementChecked,
    handleAgreementPopup,
    handleChangeTel,
    isAuthenticated,
    user,
    handleChangeEtc,
    handleChangeName,
    certificationNumber,
    handleChangeCertificationNumber,
    handleSendTel,
    count,
    status,
    isTelDisabled,
    handleSendCertificationNumber,
    handleEnableTelInput,
    workAvailableAreas,
    isOpenWorkableBottomSheet,
    handleOpenBottomSheet,
    handleCloseBottomSheet,
    currentStepData,
    consultType,
  };
};

export default useSeqConsultQuestion;
